export interface FormatNumberOptions {
	/**
	 * Number or numeric string to format
	 **/
	value: number | string;

	/**
	 * String to prepend to the formatted value
	 **/
	prefix?: string;

	/**
	 * String to append to the formatted value
	 **/
	suffix?: string;

	/**
	 * Use thousands grouping
	 * @default false
	 */
	useGrouping?: boolean;

	/**
	 * Max decimal digits (leave undefined to use style default)
	 * @default 2
	 */
	decimalScale?: number;

	/**
	 * Pad trailing zeros up to decimalScale
	 * @default false
	 */
	fixedDecimalScale?: boolean;

	/**
	 * Allows negative numbers
	 * @default true
	 */
	allowNegative?: boolean;

	/**
	 * Returned when value is empty (null/undefined/"")
	 * @default ""
	 */
	emptyValue?: string;

	/**
	 * Locale to use for formatting
	 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#options
	 * @default "hu-HU"
	 */
	locale?: Intl.LocalesArgument;

	/**
	 *  Style to use for formatting
	 *  @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#options
	 *  @default "decimal"
	 */
	numberStyle?: "decimal" | "percent" | "currency";

	/**
	 * Currency to use for formatting if numberStyle is "currency" (ISO 4217 code)
	 * @default "HUF"
	 */
	currency?: string;

	/**
	 * For percent: input 0–1 (1) or 0–100 (100) scale
	 * @default 1
	 **/
	percentInputScale?: 1 | 100;
}

const NORMALIZE = /[\s\u00A0\u202F]/g;

export const formatNumber = ({
	value,
	prefix = "",
	suffix = "",
	useGrouping = false,
	decimalScale,
	fixedDecimalScale = false,
	allowNegative = true,
	locale = "hu-HU",
	emptyValue = "",
	numberStyle = "decimal",
	currency = "HUF",
	percentInputScale = 1,
}: FormatNumberOptions): string => {
	if (value === undefined || value === null || String(value).trim() === "") {
		return emptyValue;
	}

	let num: number;
	if (typeof value === "number") {
		num = value;
	} else {
		let str = value.trim();

		// 1) remove all spaces / NBSPs
		str = str.replace(NORMALIZE, "");

		// 2) if there are multiple commas, remove all
		if ((str.match(/,/g) || []).length > 1) {
			str = str.replace(/,/g, "");
		}

		// 3) if there are multiple dots, remove all
		if ((str.match(/\./g) || []).length > 1) {
			str = str.replace(/\./g, "");
		}

		// 4) if there are multiple separators (comma + dot), the last one is the decimal separator
		const lastComma = str.lastIndexOf(",");
		const lastDot = str.lastIndexOf(".");
		if (lastComma > -1 || lastDot > -1) {
			str = str.replace(lastComma > lastDot ? /\./g : /,/g, "").replace(",", ".");
		}

		num = Number.parseFloat(str);
	}

	// If the value is NaN, return the prefix, value, and suffix
	if (isNaN(num)) {
		return `${prefix}${String(value)}${suffix}`;
	}

	if (Object.is(num, -0)) {
		num = 0;
	}

	if (numberStyle === "percent" && percentInputScale === 100) {
		num = num / 100;
	}

	// Handle negative values
	if (!allowNegative && num < 0) {
		num = Math.abs(num);
	}

	// Use Intl.NumberFormat for formatting
	const formatter = new Intl.NumberFormat(locale, {
		minimumFractionDigits: fixedDecimalScale && decimalScale !== undefined ? decimalScale : 0,
		maximumFractionDigits: decimalScale ?? 20,
		useGrouping,
		style: numberStyle,
		currency: numberStyle === "currency" ? currency : undefined,
	});

	const formatted = formatter.format(num);

	return `${prefix}${formatted}${suffix}`;
};

export const formatCurrency = (rest: FormatNumberOptions) => {
	return formatNumber({ numberStyle: "currency", currency: "HUF", decimalScale: 0, useGrouping: true, ...rest });
};

export const formatPercent = ({ decimalScale = 2, ...rest }: FormatNumberOptions) => {
	return formatNumber({ numberStyle: "percent", decimalScale, ...rest });
};
